import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, RefreshCw, User, Calendar, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "signaclean_admin_auth";

function decodeStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function AdminPage() {
  const [creds, setCreds] = useState(decodeStored());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchQuotes = useCallback(async (c) => {
    if (!c) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/quotes`, {
        auth: { username: c.username, password: c.password },
      });
      setQuotes(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Identifiants invalides.");
        localStorage.removeItem(STORAGE_KEY);
        setCreds(null);
      } else {
        setError("Erreur lors du chargement des demandes.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (creds) fetchQuotes(creds);
  }, [creds, fetchQuotes]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Identifiants requis.");
      return;
    }
    const c = { username, password };
    try {
      setLoading(true);
      await axios.get(`${API}/quotes`, { auth: c });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
      setCreds(c);
      setUsername("");
      setPassword("");
      toast.success("Connexion réussie.");
    } catch (err) {
      toast.error(
        err?.response?.status === 401
          ? "Identifiants incorrects."
          : "Erreur de connexion."
      );
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCreds(null);
    setQuotes([]);
    toast.success("Déconnecté.");
  };

  if (!creds) {
    return (
      <main data-testid="admin-page" className="pt-32 pb-24 px-5 sm:px-8">
        <div className="max-w-md mx-auto p-8 sm:p-10 rounded-2xl border border-white/10 bg-[#0d0d0d] neon-glow-soft">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
              <Lock size={18} />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30]">
                · Admin
              </div>
              <h1 className="font-display text-2xl font-extrabold">Accès restreint</h1>
            </div>
          </div>
          <form onSubmit={onLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
                Identifiant
              </label>
              <input
                data-testid="admin-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#9DEB30]/60 focus:ring-2 focus:ring-[#9DEB30]/20"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
                Mot de passe
              </label>
              <input
                data-testid="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#9DEB30]/60 focus:ring-2 focus:ring-[#9DEB30]/20"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full btn-neon disabled:opacity-60"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
          {error && (
            <p className="mt-4 text-sm text-red-400 text-center">{error}</p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="pt-32 pb-24 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-2">
              · Admin · Devis reçus
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">
              {quotes.length} demande{quotes.length > 1 ? "s" : ""}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => fetchQuotes(creds)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:border-[#9DEB30]/40"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Rafraîchir
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:border-red-500/40 text-red-400"
            >
              <LogOut size={14} />
              Déconnexion
            </button>
          </div>
        </div>

        {error && (
          <p className="mb-6 text-sm text-red-400">{error}</p>
        )}

        {quotes.length === 0 && !loading ? (
          <div className="p-10 rounded-2xl border border-white/10 text-center text-zinc-400">
            Aucune demande pour le moment.
          </div>
        ) : (
          <div className="grid gap-4">
            {quotes.map((q) => (
              <article
                key={q.id}
                data-testid={`admin-quote-${q.id}`}
                className="p-6 rounded-2xl border border-white/10 bg-[#0d0d0d] hover:border-[#9DEB30]/30 transition"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                      <User size={14} />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{q.name}</div>
                      <div className="text-xs font-mono text-zinc-500">{q.service}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Calendar size={12} />
                    {new Date(q.created_at).toLocaleString("fr-FR")}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-mono ${q.email_sent ? "bg-[#9DEB30]/15 text-[#9DEB30]" : "bg-zinc-800 text-zinc-400"}`}>
                      {q.email_sent ? "Email envoyé" : "DB only"}
                    </span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm mb-3">
                  <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#9DEB30]">
                    <Mail size={12} className="text-[#9DEB30]" /> {q.email}
                  </a>
                  <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#9DEB30]">
                    <Phone size={12} className="text-[#9DEB30]" /> {q.phone}
                  </a>
                </div>
                <div className="flex gap-2 text-sm text-zinc-300">
                  <MessageSquare size={14} className="text-[#9DEB30] mt-0.5 shrink-0" />
                  <p className="whitespace-pre-wrap">{q.message}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
